<template>
  <div class="chat-container">
    <CustomerServiceChat
      v-if="status === 'ready'"
      :style="{ width: '600px', height: '96vh', margin: '1px auto', boxShadow: '0 5px 20px #ccc' }"
    />

    <div v-else-if="status === 'loading'" class="placeholder">
      Connecting to secure server...
    </div>

    <div v-else-if="status === 'error'" class="error-msg">
      <p>Authentication failed. Please refresh the page.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import TUICustomerServer, { CustomerServiceChat } from './ai-desk-customer-vue';
import { onMounted, ref, onUnmounted } from "vue";

const status = ref<'loading' | 'ready' | 'error'>('loading');
let authTimeout: ReturnType<typeof setTimeout>;

const isReady = ref(false);

const isSdkReady = ref(false);
let pendingClearUnread = false;


const lastCredentials = ref<{ sdkAppId: number; userID: string; userSig: string } | null>(null);

const handleMessage = async (event: MessageEvent) => {
  // SECURITY CHECK: Ensure the message is actually coming from your website
  console.log("message receive: ",event.data.type);
  const trustedDomains = ['ballys.itone.biz', 'ballysbet.com', 'bellagio.itone.biz', 'win365casino.com', 'localhost'];

    //https://ballys.itone.biz
  const domain = new URL(event.origin).hostname || "";

   if (trustedDomains.indexOf(domain) == -1){
       console.log("unauthorized domain: ",domain);
       return;
   }

  try {

    if (event.data && event.data.type === 'INIT_CHAT') {
      if (!event.data.userID || !event.data.userSig) {
        throw new Error("Missing credentials in message");
      }
      console.log("SDKAppID: ",event.data.sdkAppId);
      clearTimeout(authTimeout);
      lastCredentials.value = event.data;
      await TUICustomerServer.initWithProfile({
        SDKAppID: event.data.sdkAppId,
        userID: event.data.userID,
        userSig: event.data.userSig,
        nickName: event.data.userID
      });
      isReady.value = true; // Show the widget
      status.value = 'ready';
      isSdkReady.value = true;
      if (pendingClearUnread) {
        TUICustomerServer.clearConversationUnreadCount();
        pendingClearUnread = false;
      }      
    }
    if (event.data?.type === 'CLEAR_UNREAD') {
      if (isSdkReady.value) {
        TUICustomerServer.clearConversationUnreadCount();
      } else {
        // login is still in flight — apply it as soon as init finishes above
        pendingClearUnread = true;
      }
    }

    if (event.data?.type === 'CHAT_PANEL_CLOSED') {
      await TUICustomerServer.unInit();
    }

    if (event.data?.type === 'CHAT_PANEL_REOPENED') {
      if (!lastCredentials.value) {
        console.warn('CHAT_PANEL_REOPENED received before any credentials were set');
        return;
      }      
      await TUICustomerServer.initWithProfile({
        SDKAppID: lastCredentials.value.sdkAppId,
        userID: lastCredentials.value.userID,
        userSig: lastCredentials.value.userSig,
        nickName: lastCredentials.value.userID
      });
      isSdkReady.value = true;
      if (pendingClearUnread) {
        TUICustomerServer.clearConversationUnreadCount();
        pendingClearUnread = false;
      }      
    }
        
  } catch (error) {
    console.error('Chat initialization failed:', error);
    status.value = 'error';
  }
}

onMounted(() => {
  // Start listening for messages from the parent website
  window.addEventListener("message", handleMessage);
  authTimeout = setTimeout(() => {
      if (status.value === 'loading') {
        console.warn("Chat Widget: Credential timeout reached.");
        status.value = 'error';
      }
    }, 5000);
});

onUnmounted(() => {
  // Clean up the listener when the component is destroyed
  window.removeEventListener("message", handleMessage);
  clearTimeout(authTimeout);
});

</script>

<style scoped>
.error-msg {
  text-align: center;
  padding: 20px;
  color: #ff4d4f;
  border: 1px solid #ff4d4f;
  border-radius: 8px;
  background: #fff2f0;
}
.placeholder {
  text-align: center;
  padding: 50px;
  color: #666;
}
</style>